/*
  Fig & Bloom — PostHog session recording policy.

  Capture, identify, and storefront commerce events stay on (see
  snippets/fb-posthog.liquid and assets/fb-posthog-commerce.js). Session
  recording is the expensive part: rrweb walks the DOM and hangs listeners on
  every input, which on this store fights the cart drawer's note / gift-message
  fields and was measured at ~2s on /cart.

  Rules, all of them:
    1. Cart and checkout init with disable_session_recording: true. Everywhere
       else the recorder module is allowed to load, then loaded() stops it
       until this file decides to start. disable_session_recording at init on
       a recordable page prevents the module from fetching, and later
       startSessionRecording() is a no-op (UAT 1497665).
    2. Cart, checkout, and an open cart drawer never record.
    3. Everywhere else, recording is sampled (default 5%) and starts only
       after window load + requestIdleCallback AND posthog.__loaded.
    4. ?ph_record=1 force-on starts with explicit ingestion overrides
       {sampling, linked_flag, url_trigger, event_trigger} so project
       event triggers (this project: Product Added) cannot keep
       sessionRecordingStarted() false. Retries until started or ~10s
       AFTER remote config is present. The array.js snippet stub is
       replaced on load — never call start on the captured stub.
       Live detection is __loaded + set_config (array.js has no get_config).

  Paste window.__fbPhRecordingDebug after UAT.
*/
(function (root) {
  'use strict';

  var SAMPLE_KEY = 'fb_ph_record_sample';
  var OVERRIDE_KEY = 'fb_ph_record';
  var DEFAULT_SAMPLE = 0.05;
  var IDLE_TIMEOUT_MS = 4000;
  var START_RETRY_MS = 250;
  var START_TIMEOUT_MS = 10000;
  var LOAD_RETRY_MS = 125;
  var LOAD_TIMEOUT_MS = 10000;
  var REMOTE_CONFIG_KEY = '$session_recording_remote_config';
  var EVENT_TRIGGER_OVERRIDE_KEY = '$replay_override_event_trigger';
  var FORCE_OVERRIDES = {
    sampling: true,
    linked_flag: true,
    url_trigger: true,
    event_trigger: true
  };

  var loadedWaiters = [];

  function debugObject(win) {
    var target = win || root;
    if (!target.__fbPhRecordingDebug) {
      target.__fbPhRecordingDebug = {
        forced: null,
        blocked: false,
        loaded: false,
        started: false,
        lastError: null,
        configDisabled: null,
        hasSessionRecording: null,
        recordingStatus: null,
        remoteConfig: null,
        eventTriggerOverride: null
      };
    }
    return target.__fbPhRecordingDebug;
  }

  function noteError(debug, e) {
    var msg = e == null ? 'unknown' : e.message || String(e);
    if (debug) debug.lastError = msg;
  }

  function isSnippetStub(ph) {
    if (!ph) return false;
    // us-assets array.js sets __loaded on the same object that still has
    // leftover `_i` (and may still look like an Array). That is live.
    if (ph.__loaded) return false;
    if (Object.prototype.toString.call(ph) === '[object Array]') return true;
    if (ph._i) return true;
    return false;
  }

  function isLivePosthog(ph) {
    // array.js ships set_config / startSessionRecording / __loaded.
    // It does not ship get_config — requiring that treated the live
    // instance as the snippet stub forever (UAT 360bd10).
    if (!ph || !ph.__loaded) return false;
    return typeof ph.set_config === 'function' || typeof ph.startSessionRecording === 'function';
  }

  function resolvePosthog(opts) {
    var options = opts || {};
    var win = options.window || root;
    var candidates = [options.posthog, win && win.posthog];
    var i;
    var ph;
    for (i = 0; i < candidates.length; i++) {
      ph = candidates[i];
      if (ph && ph.__loaded && isLivePosthog(ph)) return ph;
    }
    for (i = 0; i < candidates.length; i++) {
      ph = candidates[i];
      if (isLivePosthog(ph)) return ph;
    }
    return options.posthog || (win && win.posthog) || null;
  }

  function recordingStatusOf(posthog) {
    try {
      if (posthog && posthog.sessionRecording && posthog.sessionRecording.status != null) {
        return posthog.sessionRecording.status;
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return null;
  }

  function remoteConfigOf(posthog) {
    try {
      if (posthog && typeof posthog.get_property === 'function') {
        return posthog.get_property(REMOTE_CONFIG_KEY);
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return null;
  }

  function eventTriggerOverrideOf(posthog) {
    try {
      if (posthog && typeof posthog.get_property === 'function') {
        return posthog.get_property(EVENT_TRIGGER_OVERRIDE_KEY);
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return null;
  }

  function projectRecordingDisabled(posthog) {
    var cfg = remoteConfigOf(posthog);
    return !!(cfg && cfg.enabled === false);
  }

  function remoteConfigReady(posthog) {
    if (!isLivePosthog(posthog)) return false;
    var status = recordingStatusOf(posthog);
    if (status === 'awaiting_config' || status === 'missing_config') return false;
    var cfg = remoteConfigOf(posthog);
    return cfg != null;
  }

  function enrichDebug(debug, posthog) {
    if (!debug) return debug;
    debug.loaded = !!(posthog && posthog.__loaded) || !!(root.fbPosthogLoaded && isLivePosthog(posthog));
    debug.hasSessionRecording = !!(posthog && posthog.sessionRecording);
    debug.recordingStatus = recordingStatusOf(posthog);
    debug.remoteConfig = remoteConfigOf(posthog);
    debug.eventTriggerOverride = eventTriggerOverrideOf(posthog);
    debug.configDisabled = configDisabled(posthog);
    debug.started = isRecordingStarted(posthog);
    return debug;
  }

  function pageBlocksRecording(data) {
    if (!data) return false;
    if (data.isCartPage === true || data.isCheckoutPage === true) return true;
    var type = data.pageType;
    return type === 'cart' || type === 'checkout';
  }

  function recordingEnabled(data) {
    return !data || data.recordingEnabled !== false;
  }

  function sampleRate(data) {
    var rate = data && data.recordingSampleRate;
    if (typeof rate !== 'number' || isNaN(rate)) return DEFAULT_SAMPLE;
    if (rate < 0) return 0;
    if (rate > 1) return 1;
    return rate;
  }

  function matchRecordParam(source) {
    var match = String(source || '').match(/[?&]ph_record=([01])(?:&|#|$)/);
    return match ? match[1] : null;
  }

  function persistRecordingOverride(store, search, href) {
    try {
      var value = matchRecordParam(search) || matchRecordParam(href);
      if (value && store && typeof store.setItem === 'function') {
        store.setItem(OVERRIDE_KEY, value);
        return value;
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return null;
  }

  function recordingOverride(store, search, href) {
    try {
      persistRecordingOverride(store, search, href);
      if (store && typeof store.getItem === 'function') {
        var forced = store.getItem(OVERRIDE_KEY);
        if (forced === '1') return true;
        if (forced === '0') return false;
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return null;
  }

  function decideSample(store, rate, random) {
    try {
      if (store && typeof store.getItem === 'function') {
        var existing = store.getItem(SAMPLE_KEY);
        if (existing === '1') return true;
        if (existing === '0') return false;
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    var roll = typeof random === 'function' ? random() : Math.random();
    var hit = roll < rate;
    try {
      if (store && typeof store.setItem === 'function') store.setItem(SAMPLE_KEY, hit ? '1' : '0');
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return hit;
  }

  function scheduler(opts) {
    var win = (opts && opts.window) || root;
    if (opts && typeof opts.schedule === 'function') return opts.schedule;
    return function (fn, ms) {
      return (win && typeof win.setTimeout === 'function' ? win.setTimeout : setTimeout)(fn, ms);
    };
  }

  function clock(opts) {
    if (opts && typeof opts.now === 'function') return opts.now;
    return Date.now;
  }

  function whenIdleAfterLoad(win, doc, fn, idleTimeout) {
    var timeout = idleTimeout == null ? IDLE_TIMEOUT_MS : idleTimeout;
    var ran = false;
    function once() {
      if (ran) return;
      ran = true;
      if (win && typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(function () {
          fn();
        }, { timeout: timeout });
      } else if (win && typeof win.setTimeout === 'function') {
        win.setTimeout(fn, 0);
      } else {
        fn();
      }
    }
    if (!doc || doc.readyState === 'complete') {
      once();
      return;
    }
    if (win && typeof win.addEventListener === 'function') {
      win.addEventListener('load', once);
    } else {
      once();
    }
  }

  function libraryIsLoaded(posthog, win) {
    var live = resolvePosthog({ posthog: posthog, window: win || root });
    if (live && live.__loaded && isLivePosthog(live)) return true;
    if (win && win.fbPosthogLoaded && isLivePosthog(live)) return true;
    return false;
  }

  function onLibraryLoaded(ph) {
    var debug = debugObject(root);
    var live = resolvePosthog({ posthog: ph, window: root });
    enrichDebug(debug, live);
    debug.loaded = true;
    var q = loadedWaiters.splice(0);
    var i;
    for (i = 0; i < q.length; i++) q[i](live);
  }

  function whenPosthogLoaded(posthog, fn, opts) {
    var win = (opts && opts.window) || root;
    var debug = debugObject(win);

    function liveOrGiven() {
      return resolvePosthog({ posthog: posthog, window: win });
    }

    if (libraryIsLoaded(posthog, win)) {
      enrichDebug(debug, liveOrGiven());
      fn(liveOrGiven());
      return;
    }

    var called = false;
    function once(ph) {
      if (called) return;
      called = true;
      var live = resolvePosthog({ posthog: ph || posthog, window: win });
      enrichDebug(debug, live);
      fn(live);
    }

    loadedWaiters.push(once);

    var schedule = scheduler(opts);
    var now = clock(opts);
    var retry = opts && opts.loadRetry != null ? opts.loadRetry : LOAD_RETRY_MS;
    var timeout = opts && opts.loadTimeout != null ? opts.loadTimeout : LOAD_TIMEOUT_MS;
    var deadline = now() + timeout;

    function check() {
      if (called) return;
      if (libraryIsLoaded(posthog, win)) {
        once(liveOrGiven());
        return;
      }
      if (now() >= deadline) {
        noteError(debug, 'posthog.__loaded never became true');
        once(liveOrGiven());
        return;
      }
      schedule(check, retry);
    }
    schedule(check, retry);
  }

  function configDisabled(posthog) {
    try {
      // array.js has no get_config. The live flag lives on posthog.config.
      if (posthog && posthog.config) {
        return posthog.config.disable_session_recording === true;
      }
    } catch (e) {
      noteError(debugObject(root), e);
    }
    return null;
  }

  function isRecordingStarted(posthog) {
    if (!posthog || isSnippetStub(posthog)) return false;
    if (typeof posthog.sessionRecordingStarted !== 'function') {
      // Live instance without a probe: a start() call is the confirmation.
      return isLivePosthog(posthog) || !!posthog.__loaded;
    }
    try {
      return posthog.sessionRecordingStarted() === true;
    } catch (e) {
      noteError(debugObject(root), e);
      return false;
    }
  }

  function stopRecording(posthog) {
    if (!posthog || typeof posthog.stopSessionRecording !== 'function') return;
    try {
      posthog.stopSessionRecording();
    } catch (e) {
      noteError(debugObject(root), e);
    }
    var debug = debugObject(root);
    debug.started = false;
  }

  function startRecording(posthog, force, debug) {
    var live = resolvePosthog({ posthog: posthog });
    if (!live) {
      noteError(debug, 'posthog missing');
      return;
    }
    if (isSnippetStub(live)) {
      noteError(debug, 'posthog is still the array.js snippet stub');
      enrichDebug(debug, live);
      return;
    }
    try {
      if (typeof live.set_config === 'function') {
        live.set_config({ disable_session_recording: false });
      }
      if (typeof live.startSessionRecording !== 'function') {
        noteError(debug, 'startSessionRecording missing');
        enrichDebug(debug, live);
        return;
      }
      if (force) {
        // startSessionRecording(true) already sets event_trigger in current
        // array.js. Keep the explicit FORCE_OVERRIDES object anyway so a
        // future defaults change cannot re-arm the Product Added trigger.
        live.startSessionRecording(FORCE_OVERRIDES);
      } else {
        live.startSessionRecording({ sampling: true });
      }
      enrichDebug(debug, live);
    } catch (e) {
      noteError(debug, e);
      enrichDebug(debug, live);
    }
  }

  function startFailureMessage(posthog, timeout) {
    if (!isLivePosthog(posthog)) {
      return 'live posthog instance never replaced the array.js snippet stub';
    }
    if (projectRecordingDisabled(posthog)) {
      return 'project session recording disabled (remote config enabled=false)';
    }
    if (!posthog.sessionRecording) {
      return 'posthog.sessionRecording is null; start overrides could not apply';
    }
    var status = recordingStatusOf(posthog);
    if (status === 'awaiting_config' || status === 'missing_config') {
      return 'session recording status is ' + status + ' (remote config not ready)';
    }
    if (remoteConfigOf(posthog) == null) {
      return 'missing $session_recording_remote_config (recorder will not start)';
    }
    return 'sessionRecordingStarted() still false after ' + timeout + 'ms';
  }

  function startAndConfirm(opts, force, debug) {
    var schedule = scheduler(opts);
    var now = clock(opts);
    var timeout = opts.startTimeout != null ? opts.startTimeout : START_TIMEOUT_MS;
    var interval = opts.startRetry != null ? opts.startRetry : START_RETRY_MS;
    var configDeadline = now() + timeout;
    var startDeadline = null;
    debug.retries = 0;

    function tick() {
      if (debug.blocked) return;
      var posthog = resolvePosthog(opts);
      opts.posthog = posthog;
      startRecording(posthog, force, debug);
      enrichDebug(debug, posthog);
      if (debug.started) {
        debug.lastError = null;
        return;
      }

      // Do not count the "started still false" clock until the live instance
      // has remote config (or a status other than awaiting/missing) and a
      // sessionRecording object. startIfEnabledOrStop is a no-op until then.
      var ready = remoteConfigReady(posthog) && posthog && posthog.sessionRecording;
      if (!ready) {
        if (now() >= configDeadline) {
          debug.lastError = startFailureMessage(posthog, timeout);
          return;
        }
        debug.retries += 1;
        schedule(tick, interval);
        return;
      }

      if (!startDeadline) startDeadline = now() + timeout;
      if (now() >= startDeadline) {
        debug.lastError = startFailureMessage(posthog, timeout);
        return;
      }
      debug.retries += 1;
      schedule(tick, interval);
    }
    tick();
  }

  function applyPolicy(opts) {
    var options = opts || {};
    var data = options.data || {};
    var win = options.window || root;
    var posthog = resolvePosthog(options);
    options.posthog = posthog;
    var store = options.store;
    var search = options.search;
    var href = options.href;
    var random = options.random;
    var debug = debugObject(win);
    var forced = recordingOverride(store, search, href);

    debug.forced = forced;
    enrichDebug(debug, posthog);

    if (pageBlocksRecording(data)) {
      debug.blocked = true;
      debug.started = false;
      stopRecording(posthog);
      return { started: false, blocked: true, sampled: false };
    }

    debug.blocked = false;

    if (forced === false || (forced == null && !recordingEnabled(data))) {
      debug.started = false;
      stopRecording(posthog);
      return { started: false, blocked: false, sampled: false };
    }

    var sampled = forced === true ? true : decideSample(store, sampleRate(data), random);
    if (!sampled) {
      debug.started = false;
      stopRecording(posthog);
      return { started: false, blocked: false, sampled: false };
    }

    whenPosthogLoaded(
      posthog,
      function () {
        startAndConfirm(options, forced === true, debug);
      },
      options
    );
    return { started: true, blocked: false, sampled: true, forced: forced === true };
  }

  function drawerIsOpen(el) {
    if (!el) return false;
    var className = typeof el.className === 'string' ? el.className : '';
    if (el.classList && typeof el.classList.contains === 'function') {
      return el.classList.contains('active');
    }
    return (' ' + className + ' ').indexOf(' active ') !== -1;
  }

  function watchCartDrawer(doc, posthog) {
    if (!doc) return;

    function live() {
      return resolvePosthog({ posthog: posthog, window: root });
    }

    function stopIfOpen(el) {
      if (drawerIsOpen(el)) {
        var debug = debugObject(root);
        debug.blocked = true;
        stopRecording(live());
      }
    }

    var drawer = typeof doc.getElementById === 'function' ? doc.getElementById('offcanvas__mini_cart') : null;
    if (drawer && typeof MutationObserver === 'function') {
      stopIfOpen(drawer);
      new MutationObserver(function () {
        stopIfOpen(drawer);
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    if (typeof doc.addEventListener === 'function') {
      doc.addEventListener('click', function (event) {
        var target = event && event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (target.closest('open-minicart') || target.closest('#offcanvas__mini_cart')) {
          var debug = debugObject(root);
          debug.blocked = true;
          stopRecording(live());
        }
      });
    }
  }

  function boot(opts) {
    var options = opts || {};
    var win = options.window || root;
    var doc = options.document || (win && win.document);
    var data = options.data || (win && win.fbPosthogData) || {};
    var posthog = resolvePosthog(options);
    options.posthog = posthog;
    var store = options.store || (win && win.sessionStorage);
    var search = options.search != null ? options.search : (win && win.location && win.location.search);
    var href = options.href != null ? options.href : (win && win.location && win.location.href);
    var debug = debugObject(win);

    persistRecordingOverride(store, search, href);
    debug.forced = recordingOverride(store, search, href);
    debug.blocked = pageBlocksRecording(data);
    enrichDebug(debug, posthog);
    debug.started = false;

    if (debug.blocked || debug.forced === false || (debug.forced == null && !recordingEnabled(data))) {
      stopRecording(posthog);
    }

    whenIdleAfterLoad(
      win,
      doc,
      function () {
        applyPolicy({
          data: data,
          posthog: resolvePosthog({ posthog: posthog, window: win }),
          store: store,
          search: search,
          href: href,
          random: options.random,
          window: win,
          schedule: options.schedule,
          now: options.now,
          loadRetry: options.loadRetry,
          loadTimeout: options.loadTimeout,
          startRetry: options.startRetry,
          startTimeout: options.startTimeout
        });
      },
      options.idleTimeout
    );

    watchCartDrawer(doc, posthog);
    return true;
  }

  var api = {
    SAMPLE_KEY: SAMPLE_KEY,
    OVERRIDE_KEY: OVERRIDE_KEY,
    DEFAULT_SAMPLE: DEFAULT_SAMPLE,
    IDLE_TIMEOUT_MS: IDLE_TIMEOUT_MS,
    START_TIMEOUT_MS: START_TIMEOUT_MS,
    FORCE_OVERRIDES: FORCE_OVERRIDES,
    REMOTE_CONFIG_KEY: REMOTE_CONFIG_KEY,
    EVENT_TRIGGER_OVERRIDE_KEY: EVENT_TRIGGER_OVERRIDE_KEY,
    pageBlocksRecording: pageBlocksRecording,
    resolvePosthog: resolvePosthog,
    isSnippetStub: isSnippetStub,
    remoteConfigReady: remoteConfigReady,
    enrichDebug: enrichDebug,
    recordingEnabled: recordingEnabled,
    sampleRate: sampleRate,
    persistRecordingOverride: persistRecordingOverride,
    recordingOverride: recordingOverride,
    decideSample: decideSample,
    whenIdleAfterLoad: whenIdleAfterLoad,
    whenPosthogLoaded: whenPosthogLoaded,
    onLibraryLoaded: onLibraryLoaded,
    applyPolicy: applyPolicy,
    boot: boot,
    stopRecording: stopRecording,
    startRecording: startRecording
  };

  root.fbPosthogRecording = api;
  debugObject(root);

  if (!root.fbPosthogRecordingManual && root.fbPosthogData && root.posthog) {
    boot({
      window: root,
      document: root.document,
      data: root.fbPosthogData,
      posthog: root.posthog,
      store: root.sessionStorage
    });
  }
})(typeof window !== 'undefined' ? window : this);
